import { ref, computed } from 'vue'
import httpClient from '../services/httpClient'
import api from '../services/api'
import type {
  ResponseModel,
  AuthStartResponse,
  AuthCallbackRequest,
  PkceSession,
  AuhTokenResponse,
} from '../data/types'
import { useUser } from '../pages/preferences/composables/useUser'
import { generatePkce } from '../services/pkce'
const pkce_session_key = 'pkce_verifier'
const jwt = ref<string | null>(null)
const loading = ref(false)
const userLoaded = ref(false) // prevent multiple fetch calls
export function useAuth() {
  const isAuthenticated = computed(() => !!jwt.value)
  const user = useUser()

  async function authStart(): Promise<AuthStartResponse | null> {
    const { codeVerifier, codeChallenge } = await generatePkce()

    // Call backend with code_challenge
    const res = await httpClient.post<ResponseModel<AuthStartResponse>>(api.authStart(), { codeChallenge })

    // Save both codeVerifier and state if backend responded successfully
    if (res.data?.isSuccess && res.data.data?.state) {
      const pkceSession: PkceSession = {
        codeVerifier,
        state: res.data.data.state,
      }
      sessionStorage.setItem(pkce_session_key, JSON.stringify(pkceSession))
      return res.data.data
    }
    throw new Error(res.data.errorMessage as string)
  }

  async function authStartWithEmail(email: string): Promise<AuthStartResponse | null> {
    const { codeVerifier, codeChallenge } = await generatePkce()

    // Call backend with code_challenge
    const res = await httpClient.post<ResponseModel<AuthStartResponse>>(api.authStart(), { email, codeChallenge })

    // Save both codeVerifier and state if backend responded successfully
    if (res.data?.isSuccess && res.data.data?.state) {
      const pkceSession: PkceSession = {
        codeVerifier,
        state: res.data.data.state,
      }
      sessionStorage.setItem(pkce_session_key, JSON.stringify(pkceSession))
      return res.data.data
    }
    throw new Error(res.data.errorMessage as string)
  }

  async function authCallback(params: AuthCallbackRequest): Promise<ResponseModel<AuhTokenResponse>> {
    const pkceSessionStr = sessionStorage.getItem(pkce_session_key)
    if (!pkceSessionStr) throw new Error('No PKCE session found')

    const pkceSession: PkceSession = JSON.parse(pkceSessionStr)

    // Optional: validate state
    if (params.state !== pkceSession.state) throw new Error('Invalid state')

    const res = await httpClient.post<ResponseModel<AuhTokenResponse>>(api.token(), {
      authorizationCode: params.code,
      codeVerifier: pkceSession.codeVerifier,
    })

    // Clear code_verifier after exchanging code
    sessionStorage.removeItem(pkce_session_key)

    if (res.data?.isSuccess) {
      sessionStorage.removeItem(pkce_session_key)
      return res.data
    }
    throw new Error(res.data.errorMessage as string)
  }

  async function setAuth() {
    await user.fetchUser()
    if (user.user?.value) {
      setJwt(user.user.value.jwt)
    }
  }

  function setJwt(inputJwt: string) {
    jwt.value = inputJwt
  }

  function clearJwt() {
    user.clearUserState()
    jwt.value = null
    userLoaded.value = false
  }

  async function logout() {
    try {
      await httpClient.post(api.logOut())
    } catch (err) {
      console.warn('Logout request failed:', err)
    } finally {
      clearJwt()
    }
  }

  return {
    // state
    jwt,
    isAuthenticated,
    loading,
    error: user.error,

    // methods
    authStartWithEmail,
    authStart,
    authCallback,
    setAuth,
    setJwt,
    clearJwt,
    logout,
  }
}
